/**
 * Client error beacon without our own backend (roadmap P0-5): a GET of a 1×1 image on our nginx, read
 * from the container access log. Only technical fields go out — never the token, initData, phone,
 * plates, names or addresses. Digits in free text are masked, so a plate or phone in an error message
 * cannot leak.
 */
export interface ReportContext {
  /** URL of e.gif next to the app (respects the /dev/ base). */
  endpoint: string;
  version: string;
  platform: string;
  tgVersion: string;
  /** The screen at the moment of the error («quick-taxi», «home»), without ids. */
  screen?: () => string;
  send?: (url: string) => void;
}

const MAX_PER_SESSION = 10;

export function maskDetail(detail: string): string {
  return detail.replace(/\d/g, '#').replace(/\s+/g, ' ').slice(0, 120);
}

/**
 * Where an error happened: the script (our file name, or only the host of a foreign script) and line:col.
 * Not personal data, so digits stay — without them «Script error.» reports cannot be traced.
 */
export interface ErrorLocation {
  file: string;
  line: string;
}

/** Location from window 'error' fields; an empty filename is a cross-origin script the browser hides. */
export function locationOf(filename: string | undefined, line?: number, col?: number, origin = globalThis.location?.origin): ErrorLocation {
  return { file: scriptLabel(filename, origin), line: line ? `${line}:${col ?? 0}` : '' };
}

/** Location of the first frame of a stack (Chrome «at f (url:1:2)», Safari «f@url:1:2»). */
export function stackLocation(stack: string | undefined, origin = globalThis.location?.origin): ErrorLocation | undefined {
  const m = stack?.match(/(https?:\/\/[^\s)]+?):(\d+):(\d+)/);
  return m ? { file: scriptLabel(m[1], origin), line: `${m[2]}:${m[3]}` } : undefined;
}

function scriptLabel(url: string | undefined, origin: string | undefined): string {
  if (!url) return 'hidden';
  try {
    const u = new URL(url, origin);
    const name = u.origin === origin ? u.pathname.split('/').pop() || 'page' : u.host;
    return name.replace(/[^\w.-]/g, '').slice(0, 80) || 'page';
  } catch {
    return 'unknown';
  }
}

export function createReporter({ endpoint, version, platform, tgVersion, screen, send = defaultSend }: ReportContext) {
  const seen = new Set<string>();
  return (code: string, detail = '', where?: ErrorLocation) => {
    const d = maskDetail(detail);
    const line = where?.line.replace(/[^\d:]/g, '').slice(0, 15) ?? '';
    const key = `${code}|${d}|${where?.file ?? ''}|${line}`;
    if (seen.has(key) || seen.size >= MAX_PER_SESSION) return;
    seen.add(key);
    const q = new URLSearchParams({ c: code, v: version, p: platform, tv: tgVersion, ...(d ? { d } : {}) });
    const s = screen?.().replace(/[^a-z-]/gi, '').slice(0, 24);
    if (s) q.set('s', s);
    if (where) {
      q.set('f', where.file);
      if (line) q.set('l', line);
    }
    // A unique tail: browsers cache the image, and a cached hit never reaches the nginx log.
    q.set('r', Math.random().toString(36).slice(2, 10));
    send(`${endpoint}?${q.toString()}`);
  };
}

function defaultSend(url: string) {
  try {
    new Image().src = url;
  } catch {
    // Reporting must never break the app.
  }
}

let current: (code: string, detail?: string, where?: ErrorLocation) => void = () => {};

/** Installed once in main.tsx; a no-op in tests and before start. */
export function setReporter(reporter: (code: string, detail?: string, where?: ErrorLocation) => void) {
  current = reporter;
}

export function report(code: string, detail?: string, where?: ErrorLocation) {
  current(code, detail, where);
}
